import React, { useState, useEffect, useRef } from 'react';
import { observer } from 'mobx-react';
import { Switch, Route, Redirect, useRouteMatch, useLocation } from 'react-router-dom';
import { Spinner, Callout } from '@blueprintjs/core';
import { Box, Flex } from 'core/components';
import styled from 'styled-components';
import CustomNetworkContextCollection from './CustomNetworkContextCollection';
import ContextSidebar from './ContextSidebar';
import ViewContext from './ViewContext';
import EditContext from './EditContext';

const FullHeightContainer = styled(Box)`
  height: 100%;
  display: flex;
  flex-direction: column;
  margin-right: -16px;
`;

const AIContextSettings = () => {
  const match = useRouteMatch();
  const location = useLocation();

  const collectionRef = useRef(new CustomNetworkContextCollection());
  const collection = collectionRef.current;

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  useEffect(() => {
    collection.error = undefined;
    collection.fetch().catch((err) => {
      console.error('Failed to fetch AI Context versions:', err);
    });
  }, [collection]);

  const hasContext = collection.models.length > 0;
  const isEditMode = location.pathname.endsWith('/edit');

  if (collection.loading && !collection.hasFetched) {
    return (
      <Flex height="100%" alignItems="center" justifyContent="center">
        <Spinner size={30} />
      </Flex>
    );
  }

  if (collection.error) {
    return (
      <Box p={3}>
        <Callout intent="danger">Failed to load Custom Network Context history.</Callout>
      </Box>
    );
  }

  return (
    <FullHeightContainer>
      <Flex flex="1" minHeight={0}>
        <Box flex="1" overflow="hidden" display="flex" flexDirection="column" pt={1} pb={4} pl={4} pr={4}>
          <Switch>
            <Route path={`${match.path}/latest`} exact>
              <ViewContext
                versionId="latest"
                collection={collection}
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={toggleSidebar}
              />
            </Route>

            <Route path={`${match.path}/latest/edit`} exact>
              <EditContext versionId="latest" collection={collection} matchUrl={match.url} />
            </Route>

            <Route
              path={`${match.path}/:version/view`}
              exact
              render={({ match: routeMatch }) => (
                <ViewContext
                  versionId={routeMatch.params.version}
                  collection={collection}
                  isSidebarOpen={isSidebarOpen}
                  onToggleSidebar={toggleSidebar}
                />
              )}
            />

            <Route
              path={`${match.path}/:version/edit`}
              exact
              render={({ match: routeMatch }) => (
                <EditContext versionId={routeMatch.params.version} collection={collection} matchUrl={match.url} />
              )}
            />

            <Redirect to={`${match.path}/latest`} />
          </Switch>
        </Box>

        {!isEditMode && hasContext && isSidebarOpen && (
          <ContextSidebar collection={collection} matchUrl={match.url} onClose={() => setIsSidebarOpen(false)} />
        )}
      </Flex>
    </FullHeightContainer>
  );
};

export default observer(AIContextSettings);
